import type { ECElementEvent } from 'echarts';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import { isNumber } from 'lodash-es';
import type { JSX } from 'react';

import { CSSColors } from 'Components/Colors';

import { useStableChartOption } from './StableChartOption';
import { ChartAxis, ChartSeries, axisColor, chartColor, chartFont } from './charts.common';

export interface GroupedBarsChartProps {
    onChartClick: (item: string) => void;
    xAxisLabels: string[];
    /**
     * If provided, these labels will be used within the hover/tooltip, rather than the labels in `xAxisLabels`. For instance, if you want to show short dates on the x-axis and long dates in the tooltip, set this prop to the long dates.
     * The length of this array must be the same as the length of `xAxisLabels`.
     */
    xAxisLabelsForTooltips?: string[];
    /**
     * The y-axes for the chart. This should contain one element with a 'left' `position` if there is no `line`; or two elements with `positions` 'left' and 'right, respectively, if there is a `line`.
     * The left y-axis always corresponds with the bars and the right y-axis always corresponds with the line.
     *
     * IMPORTANT: If `line` is present, the `name` of the second element should match the `name` of the `line`. (No, there does not seem to be a good reason for these separate definitions.)
     * */
    yAxisInfo: ChartAxis[];
    /**
     * The values and styling for bars, with multiple bars per x-axis index. "Array" is used in the name for clarification purposes: "series" cannot be made plural.
     * Each bar is a "series", meaning it contains data for each x-axis index within its `data`; each element of `barsSeriesArray` should have `data` with a length equal to the length of `xAxisLabels` (and `xAxisLabelsForTooltips`, if present).
     * Earlier bars will be displayed closer to the left sides of clusters, if `groupingMode` is 'clustered', or closer to the bottoms of stacks, if `groupingMode` is 'stacked'.
     * Because the bars are grouped closely together, a `color` should be defined for each element.
     */
    barsSeriesArray: ChartSeries[];
    /**
     * An optional line chart to superimpose over the bars.
     * When the user hovers over a point on the line, the user will see the `name` and `value` from this prop, as well as the corresponding label from `xAxisLabels` or `xAxisLabelsForTooltips` (with the latter overriding the former).
     *
     * IMPORTANT: The `name` of this line should match the `name` of the second element of `yAxisInfo`. (No, there does not seem to be a good reason for these separate definitions.)
     */
    line?: ChartSeries;
    /**
     * Bars are grouped based on indices from `barsSeriesArray`. For example, if `a[]` and `b[]` are provided to `barsSeriesArray`, then `a.data[0]` and `b.data[0]` will be grouped, `a.data[1]` and `b.data[1]` will be grouped, etc.
     * Grouping happens in one of two ways:
     * * If `groupingMode` is 'clustered', bars will be shown next to each other (sharing the horizontal space of the x-axis).
     * * If `groupingMode` is 'stacked', bars will be vertically stacked on top of each other.
     *
     * IMPORTANT: These strings are used in the aria-label of the chart. Ensure they remain user-friendly.
     */
    groupingMode: 'clustered' | 'stacked';
    variant?: 'dark' | 'light';
}

/**
 * A "grouped" bar chart with vertical bars, optionally with a line chart superimposed. Grouped bars are either displayed side-by-side or stacked vertically, depending on the value of `props.groupingMode`.
 * * If bars are clustered, the common name for the chart's type is "clustered bar chart."
 * * If bars are stacked, the common name for the chart's type is "stacked bar chart."
 */
export const GroupedBarsChart = (props: GroupedBarsChartProps): JSX.Element => {
    const primaryFont = getComputedStyle(document.documentElement).getPropertyValue(chartFont);
    const labelColor = props.variant === 'light' ? CSSColors.BLUE : getComputedStyle(document.documentElement).getPropertyValue(chartColor);

    const ariaDescription = (() => {
        const getLineValueDescription = (index: number): string => {
            const dataPoint = props.line?.data[index];
            if (dataPoint === undefined) {
                return '';
            }
            const valueAsNumber = isNumber(dataPoint) ? dataPoint : dataPoint.value;
            return `, and the value of the line is ${valueAsNumber}`;
        };

        const getBarsValuesDescriptions = (xAxisIndex: number): string => {
            const labels = props.barsSeriesArray.map((bar) => bar.name);
            const values = props.barsSeriesArray.map((bar) => (isNumber(bar.data[xAxisIndex]) ? bar.data[xAxisIndex] : bar.data[xAxisIndex].value));
            return labels.map((label, index) => `${values[index]} (${label})`).join(', ');
        };

        const xAxisLabels = props.xAxisLabelsForTooltips ?? props.xAxisLabels;
        const valuesDescriptions = xAxisLabels.map((xAxisLabel, index) => `For ${xAxisLabel}, the values of the bars are ${getBarsValuesDescriptions(index)}${getLineValueDescription(index)}.`).join(' ');
        const chartType = props.line ? `A ${props.groupingMode} bar chart showing ${props.yAxisInfo[0].name}, with a superimposed line chart showing ${props.yAxisInfo[1].name}.` : `A bar chart showing ${props.yAxisInfo[0].name}.`;
        return `${chartType} ${valuesDescriptions}`;
    })();

    const getOption = (info: Omit<GroupedBarsChartProps, 'onChartClick'>, primaryFont: string, labelColor: string): EChartsOption => {
        // Add a non functional axis to complete the grid
        const yAxes: ChartAxis[] = info.yAxisInfo.length < 2 ? [...info.yAxisInfo, { name: '', position: 'right' }] : info.yAxisInfo;

        return {
            tooltip: {
                trigger: 'item',
            },
            legend: { show: false },
            textStyle: {
                fontFamily: primaryFont,
            },
            grid: {
                containLabel: true,
                left: '10%',
                right: '10%',
                top: '10%',
                bottom: '10%',
            },
            xAxis: [
                {
                    type: 'category',
                    axisTick: {
                        alignWithLabel: true,
                    },
                    data: info.xAxisLabelsForTooltips ?? info.xAxisLabels.map((label) => label.toUpperCase()),
                    axisLine: {
                        show: true,
                        lineStyle: {
                            color: axisColor,
                        },
                    },
                    axisLabel: {
                        color: labelColor,
                        formatter: info.xAxisLabelsForTooltips ? (_value: string, index: number) => info.xAxisLabels[index] : undefined,
                    },
                },
            ],
            yAxis: yAxes.map((yAxis) => ({
                type: 'value',
                name: yAxis.name?.toUpperCase(),
                position: yAxis.position,
                min: yAxis.min,
                max: yAxis.max,
                minInterval: yAxis.minInterval,
                maxInterval: yAxis.maxInterval,
                interval: yAxis.interval,
                alignTicks: true,
                show: true,
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: axisColor,
                    },
                },
                axisLabel: {
                    formatter: yAxis.labelFormatter ? (item: number): string => yAxis.labelFormatter!(item).toUpperCase() : undefined,
                    color: labelColor,
                },
                nameLocation: 'middle',
                nameGap: yAxis.labelFormatter ? 100 : 50,
                nameRotate: yAxis.position === 'left' ? 90 : 270,
                splitLine: {
                    lineStyle: {
                        color: axisColor,
                    },
                },
                nameTextStyle: {
                    color: labelColor,
                },
            })),
            series: [
                ...info.barsSeriesArray.map((barInfo) => ({
                    name: barInfo.name,
                    type: 'bar',
                    stack: info.groupingMode === 'clustered' ? undefined : 'stack',
                    emphasis: {
                        focus: 'series',
                    },
                    itemStyle: {
                        color: barInfo.color,
                    },
                    data: barInfo.data,
                    barMaxWidth: '20%',
                })),
                {
                    name: info.line?.name,
                    type: 'line',
                    itemStyle: { color: info.line?.color },
                    data: info.line?.data,
                    yAxisIndex: 1,
                    emphasis: {
                        focus: 'series',
                    },
                    symbol: 'circle',
                    symbolSize: 10,
                },
            ],
        };
    };

    // The x-axis label formatter reads `xAxisLabels`, which is not otherwise in the option when `xAxisLabelsForTooltips` is provided.
    const option = useStableChartOption(getOption(props, primaryFont, labelColor), [props.xAxisLabels]);

    return (
        <ReactECharts
            aria-label={ariaDescription}
            option={option}
            role="img"
            style={{
                height: '100%',
                width: '100%',
                minHeight: '300px',
                minWidth: '400px',
                maxHeight: '300px',
            }}
            onEvents={{ click: (params: ECElementEvent) => props.onChartClick(`${params.name} | ${params.seriesName}`) }}
        />
    );
};
